import React, { useState } from "react";
import {
  Grid as Grid,
  Typography,
  Button,
  TextField,
  Autocomplete,
  Box,
  IconButton,
  Chip,
  styled,
  LinearProgress,
  linearProgressClasses
} from "@mui/material";
import {
  FETCH_COMPANIES,
  FETCH_COMPANIES_2,
  SAVE_COMPANIES,
  SAVE_USER_COMPANIES,
  SAVE_USER_TRADES
} from "../../constant/Apipath";
import { useSelector } from "react-redux";
import { Postrequestcall } from "../../request/Postrequest";
import BuildIdLogo from "../../assets/images/build_id_logo.png";
import { BIBlue, BIGrey, BILightGrey } from "../../assets/helpers";
import { Clear, Edit } from "@mui/icons-material";
import EditTrade from "../profile/EditTrade";

export const TradeAndProfilePage = (props) => {
  const { gotoExperiencePage, openTradeProfilePage, loadingPage, setCompanyId } = props;

  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [inputValue, setInputValue] = useState(""); // Tracks input value for Autocomplete
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState(null);
  const [trades, setTrades] = useState([]);
  const [showTrade, setShowTrade] = useState(false);
  const { loginData } = useSelector((state) => state.main);
  const { accessToken } = loginData;

  const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 5,
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: theme.palette.grey[theme.palette.mode === "light" ? 200 : 800]
    },
    [`& .${linearProgressClasses.bar}`]: {
      borderRadius: 5,
      backgroundColor: theme.palette.mode === "light" ? BIBlue : "#308fe8"
    }
  }));

  const fetchCompanyList = async (inputVal) => {
    try {
      // OLD API: FETCH_COMPANIES (commented, do not delete)
      // const fetchObj = {
      //   SearchList: [{ name: inputVal }]
      // };
      // let getCompanyResponse = await Postrequestcall(FETCH_COMPANIES, fetchObj, accessToken);
      
      // NEW API: FETCH_COMPANIES_2 (active)
      const fetchObj = {
        UserId: loginData?.userInformation?.UserId ?? 0,
        SearchString: inputVal,
        PageNumber: 1,
        PageSize: 20,
        Manufacturer: false,
        Id: 0
      };
      let getCompanyResponse = await Postrequestcall(FETCH_COMPANIES_2, fetchObj, accessToken);
      if (getCompanyResponse?.status === 201) {
        let getList = getCompanyResponse?.data?.data?.map((item) => {
          return {
            id: item?.id,
            name: item?.name
          };
        });
        setCompanyOptions(getList);
        setIsAutocompleteOpen(true); // Open autocomplete if options are available
      }
    } catch (error) {
      console.log("fetchCompanyList-> Error When Fetching Companies: ", error);
    }
  };

  const handleInputChange = (event, newInputValue, reason) => {
    if (debounceTimeout) clearTimeout(debounceTimeout); // Clear the existing timeout

    if (reason === "input") {
      setInputValue(newInputValue);
      if (newInputValue?.length >= 2) {
        const newTimeout = setTimeout(() => {
          fetchCompanyList(newInputValue);
        }, 300); // Debounce for 300ms
        setDebounceTimeout(newTimeout); // Set the new timeout
      }
    }
  };

  const handleSelectOption = (event, value) => {
    setSelectedCompany(value);
    setIsAutocompleteOpen(false);
    setInputValue(value?.name);
  };

  const handleAddOption = () => {
    const newOption = { id: 0, name: inputValue };
    setCompanyOptions((prevOptions) => [...prevOptions, newOption]);
    setSelectedCompany(newOption);
    setIsAutocompleteOpen(false); // Close the autocomplete dropdown
    setInputValue(inputValue); // Reset the input field
  };

  const handleSelectTradeClick = () => {
    setShowTrade(true);
  };

  const handleClear = () => {
    setSelectedCompany(null); // Clear the selected company
    setInputValue(""); // Reset the input value
    setCompanyOptions([]);
  };

  const handleTradeProfileNextPage = async () => {
    loadingPage();
    try {
      if (trades?.length) {
        const userId = Number(loginData?.userInformation?.UserId);
        const tradesArray = trades.map(({ tradeId, isSelected, tradeName }) => ({
          Id: 0,
          UserId: userId,
          TradeId: tradeId,
          TradeType: isSelected ? "Main" : "Other",
          ModifiedBy: userId,
          tradeName
        }));

        const userTradeObj = {
          UserId: userId,
          ReturnRecordId: true,
          ReturnRecordError: true,
          SaveList: tradesArray
        };

        await Postrequestcall(SAVE_USER_TRADES, userTradeObj, accessToken);
      }
    } catch (error) {
      console.error("handleTradeProfileNextPage-> Error When Saving User Trades: ", error);
    }

    try {
      const userId = Number(loginData?.userInformation?.UserId);
      const companyId = selectedCompany?.id || 0;
      const companyName = selectedCompany?.name || "";
      const isNewCompany = companyId === 0 && companyName !== "";

      if (isNewCompany) {
        const saveCompanyObj = {
          userId,
          ReturnRecordId: true,
          ReturnRecordError: true,
          SaveList: [
            {
              Id: 0,
              Name: companyName,
              ModifiedBy: userId,
              RecordStatusId: 1,
              Manufacturer: false
            }
          ]
        };

        const saveCompanyRes = await Postrequestcall(SAVE_COMPANIES, saveCompanyObj, accessToken);
        const newCompanyId = saveCompanyRes?.data?.[0]?.objData?.id;

        if (saveCompanyRes?.data?.[0]?.message === "OK" && newCompanyId) {
          await saveUserCompany(userId, newCompanyId);
          setCompanyId(newCompanyId);
        }
      } else if (companyId > 0) {
        await saveUserCompany(userId, companyId);
        setCompanyId(companyId);
      }

      gotoExperiencePage();
    } catch (error) {
      openTradeProfilePage();
      console.error("handleTradeProfileNextPage-> Error When Saving Company: ", error);
    }
  };

  const saveUserCompany = async (userId, companyId) => {
    const userCompanyObj = {
      UserId: userId,
      ReturnRecordId: true,
      ReturnRecordError: true,
      SaveList: [
        {
          Id: 0,
          UserId: userId,
          RoleId: 0,
          CompanyId: companyId,
          Active: true,
          StartDate: null,
          EndDate: null,
          ModifiedBy: userId,
          IncludeInResume: false,
          CurrentCompany: false,
          Description: ""
        }
      ]
    };

    await Postrequestcall(SAVE_USER_COMPANIES, userCompanyObj, accessToken);
  };

  const onEdit = () => {
    setShowTrade(true);
  };

  return (
    <Box sx={{ p: 4 }}>
      <Grid container direction="column" spacing={2}>
        {/* Logo */}
        <Grid item>
          <img src={BuildIdLogo} alt="" style={{ display: "block", margin: "0 auto" }} />
        </Grid>

        <Grid item justifyContent={"center"}>
          <BorderLinearProgress variant="determinate" value={66.66} />
          <Grid item size={{ xs: 6 }} display={"flex"} justifyContent={"space-between"}>
            <Typography variant="caption" sx={{ textAlign: "left" }}>
              Trade and Company
            </Typography>
            <Typography variant="caption" sx={{ textAlign: "right" }}>
              2/3
            </Typography>
          </Grid>
        </Grid>

        {/* Title */}
        <Grid item>
          <Typography variant="h5">What do you do for work?</Typography>
          <Typography variant="body2" color="textSecondary">
            Your trade and company will help you connect with similar BuildID users
          </Typography>
        </Grid>

        {/* Select Trade Button */}
        {trades?.length > 0 ? (
          <Grid item display={"flex"} gap={0.5}>
            {trades?.map((item, index) => (
              <Grid item key={index}>
                <Chip
                  label={item?.tradeName}
                  style={{
                    background: item?.isSelected && BIBlue,
                    color: item?.isSelected && "white"
                  }}
                />
              </Grid>
            ))}
            <Grid item>
              <IconButton onClick={onEdit} sx={{ color: BIBlue }} aria-label="edit">
                <Edit />
              </IconButton>
            </Grid>
          </Grid>
        ) : (
          <Grid item align="center">
            <Button
              variant="contained"
              style={{ background: BIBlue }}
              onClick={handleSelectTradeClick}>
              SELECT MY TRADE(S)
            </Button>
          </Grid>
        )}

        {/* Search for Company Field */}
        <Grid item sx={{ width: "100%", maxWidth: 500 }}>
          <Autocomplete
            options={companyOptions}
            getOptionLabel={(option) => option?.name} // Assuming each company has a `name` field
            inputValue={inputValue}
            onInputChange={handleInputChange}
            onChange={handleSelectOption}
            open={isAutocompleteOpen} // Open only if input length >= 2
            disableClearable // Disable the default clear button of Autocomplete
            onClose={() => setIsAutocompleteOpen(false)}
            noOptionsText={
              inputValue?.length >= 2 ? (
                <Button variant="text" color="primary" onClick={handleAddOption}>
                  Add "{inputValue}"
                </Button>
              ) : (
                "Start typing to search"
              )
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search for a Company"
                variant="outlined"
                fullWidth
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {selectedCompany && (
                        <IconButton
                          onClick={() => {
                            handleClear(); // Clear input and selected option
                          }}
                          aria-label="clear"
                          edge="end">
                          <Clear />
                        </IconButton>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        </Grid>

        {/* Info Text */}
        <Grid item>
          <Typography variant="body2" align="center" color="textSecondary">
            If you are currently unemployed, leave this blank.
          </Typography>
        </Grid>

        {/* Next Button */}
        <Grid item align="center">
          <Button
            variant="contained"
            style={{
              background: trades?.length > 0 ? BIBlue : BILightGrey,
              color: trades?.length > 0 ? "white" : BIGrey
            }}
            disabled={trades?.length === 0}
            onClick={handleTradeProfileNextPage}>
            NEXT
          </Button>
        </Grid>
      </Grid>

      {showTrade && (
        <EditTrade
          userTradeList={[]}
          setUsertrades={setTrades}
          tradesExisting={trades}
          showTrade={showTrade}
          handleClose={() => setShowTrade(false)}
        />
      )}
    </Box>
  );
};
